import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import {
  ClockIcon,
  FilmIcon,
  ClipboardDocumentCheckIcon,
  BookOpenIcon,
  ArrowPathIcon,
  CpuChipIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  PhotoIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import VideoHeaderStrip, { formatDuration } from '../components/VideoHeaderStrip';
import ObservationEntry from '../components/ObservationEntry';
import { findingsAPI } from '../services/findingsApi';
import { assessmentAPI } from '../services/assessmentApi';
import { videosAPI } from '../services/videosApi';
import { photosAPI } from '../services/photosApi';
import { getApiErrorMessage } from '../utils/apiError';
import { runProgress, stageLabel } from '../utils/runProgress';
import { formatFindingRange } from '../utils/findingTime';
import { FEATURES } from '../config/phases';
import { useAuth } from '../contexts/AuthContext';
import usePublicConfig from '../hooks/usePublicConfig';

function formatUsd(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return `$${value.toFixed(2)}`;
}

/** A server time in the viewer's own time zone. The server sends UTC with no zone suffix,
 * which `new Date` reads as local time, so the Overview showed UTC unlabelled beside a
 * report in local time (QA 2026-09-29); ReportPage.parseServerTime reads it the same way. */
function formatServerTime(value) {
  if (!value) return '—';
  return new Date(/(?:[zZ]|[+-]\d\d:?\d\d)$/.test(value) ? value : `${value}Z`).toLocaleString();
}

function formatNumber(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <Icon className="h-6 w-6 text-gray-400" />
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
              <dd className="text-lg font-medium text-gray-900">{value}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

function CostTile({ icon: Icon, label, value, tint, color }) {
  return (
    <div className={`${tint} rounded-lg p-4`}>
      <div className="flex items-center">
        <div className={`${color} mr-3`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-600 truncate">{label}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}

/** 6.4's RunProgressPanel, inlined (not a separate file: this phase's YOUR FILES grants
 * only the two page files and FindingCard/VideoHeaderStrip). Renders the real empty state
 * with a working Start button when no run exists yet, the live stage while processing
 * (polled every 3 s by the caller), and the finished/failed state with a Re-run button. */
function RunProgressPanel({ status, video, onStart, isStarting, writingReport, isAdmin, demoLocked }) {
  const { state, completed, route, label: stageNow, pct: progressPct } = runProgress(status, video?.kind);

  const badgeStyle =
    state === 'done'
      ? 'bg-green-100 text-green-800'
      : state === 'failed'
      ? 'bg-red-100 text-red-800'
      : state === 'processing'
      ? 'bg-blue-100 text-blue-800'
      : 'bg-gray-100 text-gray-600';

  const canStart = video?.status === 'READY' && (!status || state === 'idle');
  // Writing the GRIHA report holds the video's lock (a re-run would 409 until it finishes).
  // The demo lock (Anshul, 2026-09-26: `demo_lock_seed_runs`) 409s a re-run of the seeded
  // clip/walkthrough videos too -- a control that always 409s is worse than no control at
  // all (2026-09-06 standing rule), so it is simply not rendered rather than rendered and
  // left to fail.
  const canRerun = (state === 'done' || state === 'failed') && !writingReport && !demoLocked;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Pipeline run</h3>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${badgeStyle}`}>
          {state}
        </span>
      </div>

      {state === 'idle' && !status?.started_at ? (
        <div className="text-center py-8">
          <ClipboardDocumentListIcon className="mx-auto h-10 w-10 text-gray-300" />
          {/* A photo set is not "a video" and has no frames to sample at an interval
              (Anshul, 2026-09-26: "they are not videos/recordings") -- its own wording
              here, and below, rather than a sentence that only makes sense for a
              walkthrough/clip/upload. */}
          <h4 className="mt-2 text-sm font-medium text-gray-900">
            {video?.kind === 'photos' ? 'This photo set has not been processed' : 'This video has not been processed'}
          </h4>
          <p className="mt-1 text-sm text-gray-500">
            {video?.kind === 'photos'
              ? 'Start the pipeline to read the photos, describe them, and check them against the standard.'
              : 'Start the pipeline to sample frames, describe them, and check them against the standard.'}
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => onStart(false)}
              disabled={!canStart || isStarting}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              {isStarting ? 'Starting…' : 'Start processing'}
            </button>
          </div>
          {!canStart && video?.status !== 'READY' && (
            <p className="mt-2 text-xs text-gray-400">
              {video?.kind === 'photos'
                ? `This photo set is not ready to process yet (status: ${video?.status || 'unknown'}).`
                : `This video is not ready to process yet (status: ${video?.status || 'unknown'}).`}
            </p>
          )}
        </div>
      ) : (
        <div>
          <p className="text-sm text-gray-700 mb-2">Stage: {stageNow}</p>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${
                state === 'failed' ? 'bg-red-500' : state === 'done' ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {completed.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {completed.map((stage) => (
                <span
                  key={stage}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-[#F8F8FF] text-gray-600 border border-gray-200"
                >
                  {stageLabel(stage, route)}
                </span>
              ))}
            </div>
          )}

          <dl className="grid grid-cols-2 gap-4 text-sm mb-4">
            <div>
              <dt className="text-gray-500">Started</dt>
              <dd className="text-gray-900">
                {formatServerTime(status?.started_at)}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Finished</dt>
              <dd className="text-gray-900">
                {formatServerTime(status?.finished_at)}
              </dd>
            </div>
          </dl>

          {state === 'failed' && status?.error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {status.error}
            </div>
          )}

          {status?.budget_exceeded && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {/* Anshul, 2026-09-26: "the auditor account shows no cost, tokens or usage
                  metrics, remove that from auditor account flow" -- this banner's admin
                  wording names a "cost budget"; a non-admin gets the same fact with no
                  dollar-shaped word in it at all. */}
              {isAdmin
                ? 'This run hit its cost budget; some observations may have been skipped.'
                : 'This run reached its budget before every shot was assessed; some observations may have been skipped.'}
            </div>
          )}

          <ArchiveLine archive={status?.archive} videoKind={video?.kind} />

          {canRerun && (
            <button
              type="button"
              onClick={() => onStart(true)}
              disabled={isStarting}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              {isStarting ? 'Starting…' : 'Re-run pipeline'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function formatBytes(bytes) {
  if (typeof bytes !== 'number' || Number.isNaN(bytes)) return '—';
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Where the run's folder was copied (RUNLOG V8: "always store everything"): frames, every
 * file, the audio and the raw transcript, under the video's prefix in the demo bucket.
 * A photo set has no audio track or transcript to speak of -- its own parenthetical
 * (Anshul, 2026-09-26: "they are not videos/recordings") rather than a claim this route
 * never produces. */
function ArchiveLine({ archive, videoKind }) {
  if (!archive) return null;
  const tone = {
    done: 'text-gray-600',
    partial: 'text-amber-800',
    failed: 'text-red-700',
    skipped: 'text-gray-500',
  }[archive.state] || 'text-gray-600';
  const contents = videoKind === 'photos' ? 'photos, results' : 'frames, audio, transcript, results';
  let text;
  if (archive.state === 'done') {
    text = `Everything this run produced is stored in S3: ${formatNumber(archive.objects)} files, ${formatBytes(archive.bytes)} (${contents}).`;
  } else if (archive.state === 'partial') {
    text = `Stored in S3 except ${formatNumber(archive.failed)} file${archive.failed === 1 ? '' : 's'}: ${archive.error || 'no reason given'}.`;
  } else if (archive.state === 'failed') {
    text = `This run's files could not be stored in S3: ${archive.error || 'no reason given'}.`;
  } else {
    text = `This run's files were not copied to S3 (${archive.error || 'switched off'}).`;
  }
  return (
    <p className={`mb-4 text-xs ${tone}`} data-testid="archive-line">
      {text}
    </p>
  );
}

/** Every shot the run examined, read-only (RUNLOG V8): the per-shot record that used to be
 * the review queue, kept here so nothing the pipeline saw is lost now that the Review Queue
 * reviews GRIHA's report rows instead. Loaded only when opened: a long walkthrough has
 * hundreds of shots. */
function ShotLog({ videoId, enabled, videoKind }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, error } = useQuery(
    ['video-findings-log', videoId],
    () => findingsAPI.getFindings(videoId, { includeNoIssue: true }),
    { enabled: enabled && open, select: (response) => response.data, retry: false }
  );
  const observations = (data?.observations || [])
    .slice()
    .sort((a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0));
  return (
    <details
      className="bg-white rounded-lg shadow-sm border border-gray-200"
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary className="cursor-pointer px-6 py-4 text-sm font-semibold text-gray-900">
        Shot-by-shot log
        <span className="ml-2 font-normal text-gray-500">
          every shot the pipeline examined, with what it saw and any finding
        </span>
      </summary>
      <div className="px-6 pb-6 space-y-4">
        {!enabled ? (
          <p className="text-sm text-gray-500">Available once the run has finished.</p>
        ) : isLoading ? (
          <div className="h-24 bg-gray-100 rounded animate-pulse" />
        ) : isError ? (
          <p className="text-sm text-red-700">{getApiErrorMessage(error, 'Could not load the shots.')}</p>
        ) : observations.length === 0 ? (
          <p className="text-sm text-gray-500">This run recorded no shots.</p>
        ) : (
          observations.map((observation, i) => (
            <ObservationEntry key={observation.group_id || i} observation={observation} videoKind={videoKind} />
          ))
        )}
      </div>
    </details>
  );
}

function CostPanel({ usage }) {
  const totals = usage?.totals || {};
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Run cost</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <CostTile
          icon={CpuChipIcon}
          label="Calls"
          value={formatNumber(totals.calls)}
          tint="bg-blue-50"
          color="text-blue-700"
        />
        <CostTile
          icon={ArrowDownTrayIcon}
          label="Input tokens"
          value={formatNumber(totals.input_tokens)}
          tint="bg-emerald-50"
          color="text-emerald-700"
        />
        <CostTile
          icon={ArrowUpTrayIcon}
          label="Output tokens"
          value={formatNumber(totals.output_tokens)}
          tint="bg-amber-50"
          color="text-amber-700"
        />
        <CostTile
          icon={CurrencyDollarIcon}
          label="Est. cost"
          value={formatUsd(totals.cost_usd)}
          tint="bg-green-50"
          color="text-green-700"
        />
      </div>
    </div>
  );
}

function AnswerKeyTable({ entries, videoKind }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Answer key</h3>
      {!Array.isArray(entries) || entries.length === 0 ? (
        <p className="text-sm text-gray-500">No answer key for this video.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#F8F8FF]">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Labels
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Caption
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-2 text-sm text-gray-500">{entry.index ?? idx + 1}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                    {formatFindingRange(entry.start_s, entry.end_s, videoKind)}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {(entry.labels || []).map((label, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-500">{entry.caption || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * A photo set's own evidence view (Anshul, 2026-09-26): a grid of its photos in place of
 * the video player, with a larger view on click -- next/previous/close, including the
 * keyboard (arrows, Escape) -- since a click that opens nothing would be exactly the dead
 * control the 2026-09-06 standing rule forbids.
 */
function PhotoGrid({ photos, isLoading, isError, error }) {
  const [openIndex, setOpenIndex] = useState(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const showNext = useCallback(
    () => setOpenIndex((i) => (i === null || photos.length === 0 ? i : (i + 1) % photos.length)),
    [photos.length]
  );
  const showPrev = useCallback(
    () => setOpenIndex((i) => (i === null || photos.length === 0 ? i : (i - 1 + photos.length) % photos.length)),
    [photos.length]
  );

  useEffect(() => {
    if (openIndex === null) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') showNext();
      else if (e.key === 'ArrowLeft') showPrev();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openIndex, close, showNext, showPrev]);

  if (isLoading) {
    return <div className="h-48 bg-gray-100 rounded-lg animate-pulse" />;
  }
  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
        {getApiErrorMessage(error, 'Could not load the photos in this set.')}
      </div>
    );
  }
  if (photos.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-sm text-gray-400 border border-dashed border-gray-300 rounded-lg">
        No photos in this set yet.
      </div>
    );
  }

  const current = openIndex !== null ? photos[openIndex] : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {photos.map((photo, i) => (
          <button
            key={photo.index}
            type="button"
            onClick={() => setOpenIndex(i)}
            className="relative rounded-md overflow-hidden border border-gray-200 aspect-square bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {/* jsx-a11y/img-redundant-alt: a screen reader already announces "image", so
                the alt text avoids the word "photo" -- the visible index badge and the
                dialog's own aria-label (below) still say "Photo N" where it matters. */}
            <img src={photo.url} alt={`Site view ${photo.index}`} className="w-full h-full object-cover" loading="lazy" />
            <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px]">
              {photo.index}
            </span>
          </button>
        ))}
      </div>

      {current && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Photo ${current.index}`}
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <XMarkIcon className="h-8 w-8" />
          </button>
          {photos.length > 1 && (
            <button
              type="button"
              onClick={showPrev}
              aria-label="Previous photo"
              className="absolute left-4 text-white hover:text-gray-300"
            >
              <ChevronLeftIcon className="h-10 w-10" />
            </button>
          )}
          <img
            src={current.url}
            alt={`Site view ${current.index}`}
            className="max-h-[85vh] max-w-[85vw] object-contain"
          />
          {photos.length > 1 && (
            <button
              type="button"
              onClick={showNext}
              aria-label="Next photo"
              className="absolute right-4 text-white hover:text-gray-300"
            >
              <ChevronRightIcon className="h-10 w-10" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function VideoOverviewPage() {
  const { projectId, videoId } = useParams();
  const queryClient = useQueryClient();
  const prevStateRef = useRef(undefined);
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const { config } = usePublicConfig();

  const {
    data: video,
    isLoading: videoLoading,
    isError: videoIsError,
    error: videoError,
  } = useQuery(['video', videoId], () => findingsAPI.getVideo(videoId), {
    enabled: !!videoId,
    select: (response) => response.data,
    retry: 1,
  });

  const { data: status } = useQuery(['video-status', videoId], () => videosAPI.getStatus(videoId), {
    enabled: !!videoId,
    select: (response) => response.data,
    retry: false,
    refetchInterval: (data) => (data?.state === 'processing' ? 3000 : false),
  });

  // Ground truth: FindingListResponse.usage is the real run-wide total (a finding's own
  // model_usage is always zeroed, and RunStatusResponse.usage can be stale mid-run), so the
  // cost tiles read from the findings document rather than the status poll. Only fetched
  // once the run is done -- GET /findings 404s ("No findings for this video") before then.
  // The GRIHA report as the Review Queue shows it (same query key, so one request serves
  // both). The run's own counts.assessment_state is frozen at run end; this is live.
  const { data: report } = useQuery(['video-assessment', videoId], () => assessmentAPI.get(videoId), {
    enabled: !!videoId && status?.state === 'done',
    select: (response) => response.data,
    retry: false,
    refetchInterval: (data) =>
      data?.assessment?.state === 'generating' || data?.attempt?.state === 'generating' ? 5000 : false,
  });
  const writingReport =
    report?.assessment?.state === 'generating' || report?.attempt?.state === 'generating';
  const reportHasRows = (report?.entries?.length || 0) > 0;
  const reportCounts = report?.counts || { total: 0, approved: 0, rejected: 0 };
  const reportDecided = reportCounts.approved + reportCounts.rejected;
  let reportStateText = 'Review the rows of the GRIHA report, then open or download the report.';
  if (report && !report.assessment) {
    reportStateText = 'The GRIHA report has not been written for this run yet; write it from the Review Queue.';
  } else if (report && !reportHasRows) {
    reportStateText = writingReport
      ? 'The GRIHA report is being written; this takes a few minutes.'
      : 'The GRIHA report could not be written; try again from the Review Queue.';
  } else if (report && reportDecided === reportCounts.total) {
    reportStateText = 'Every row of the GRIHA report has been reviewed: open or download the report.';
  } else if (report) {
    reportStateText = `${reportDecided} of ${reportCounts.total} rows of the GRIHA report reviewed. Review the rest, then open or download the report.`;
  }

  const { data: findingsDoc } = useQuery(
    ['video-findings', videoId],
    () => findingsAPI.getFindings(videoId),
    {
      enabled: !!videoId && status?.state === 'done',
      select: (response) => response.data,
      retry: false,
    }
  );

  // The photo grid replaces the video player for a photo set (Anshul, 2026-09-26); fetched
  // only once `video` has loaded and named its kind, since a walkthrough/clip/upload has no
  // /photos route to call.
  const {
    data: photosDoc,
    isLoading: photosLoading,
    isError: photosIsError,
    error: photosError,
  } = useQuery(['video-photos', videoId], () => photosAPI.list(videoId), {
    enabled: !!videoId && video?.kind === 'photos',
    select: (response) => response.data,
    retry: false,
  });
  const photos = photosDoc?.photos || [];

  // The demo lock (Anshul, 2026-09-26: `demo_lock_seed_runs`) only ever 409s a re-run of
  // the two seeded kinds, never a fresh READY upload, so Start is unaffected. `config` is
  // `undefined` both before /config/public has answered and if it never does at all
  // (usePublicConfig never guesses a default); treating that as "unlocked" for those few
  // seconds let Re-run flash on a seeded clip/walkthrough video before 409ing the click.
  // Only those two kinds are ever affected either way, so a plain upload's Re-run is never
  // held back by a config fetch that has nothing to do with it.
  const isSeedKind = video?.kind === 'clip' || video?.kind === 'walkthrough';
  const demoLocked = isSeedKind && (config === undefined || !!config.demo_lock_seed_runs);

  // Processing -> done/failed transition toast (6.5's VideoLayout watcher; that shell is
  // retired per 19.3 point 9, so this page — the one that owns the Start button and the
  // status poll — carries the watch instead).
  useEffect(() => {
    const prev = prevStateRef.current;
    const next = status?.state;
    if (prev === 'processing' && next === 'done') {
      toast.success('Processing finished');
      queryClient.invalidateQueries(['video', videoId]);
      queryClient.invalidateQueries(['video-findings', videoId]);
    } else if (prev === 'processing' && next === 'failed') {
      toast.error(status?.error || 'Processing failed');
      queryClient.invalidateQueries(['video', videoId]);
    }
    prevStateRef.current = next;
  }, [status?.state, status?.error, queryClient, videoId]);

  const startMutation = useMutation((force) => videosAPI.process(videoId, force ? { force: true } : {}), {
    onMutate: async (force) => {
      await queryClient.cancelQueries(['video-status', videoId]);
      queryClient.setQueryData(['video-status', videoId], (prev) => ({
        ...(prev || { video_id: videoId }),
        state: 'processing',
        stage: prev?.stage && force ? prev.stage : null,
        error: null,
      }));
    },
    onSuccess: () => {
      toast.success('Processing started');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Could not start processing'));
      queryClient.invalidateQueries(['video-status', videoId]);
    },
    onSettled: () => {
      queryClient.invalidateQueries(['video-status', videoId]);
      queryClient.invalidateQueries(['video', videoId]);
    },
  });

  if (videoLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-40 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <VideoHeaderStrip
        video={video}
        isLoading={videoLoading}
        isError={videoIsError}
        error={videoError}
        projectId={projectId}
        videoId={videoId}
        activeTab="overview"
      />

      {videoIsError ? (
        <div className="max-w-lg rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
          {videoError?.response?.data?.detail || 'This video could not be loaded.'}
        </div>
      ) : (
        <div className="space-y-6">
          {video?.kind === 'photos' ? (
            <PhotoGrid photos={photos} isLoading={photosLoading} isError={photosIsError} error={photosError} />
          ) : (
            <div className="bg-black rounded-lg overflow-hidden border border-gray-200">
              {video?.file_url ? (
                <video
                  src={video.file_url}
                  poster={video.thumbnail_url || undefined}
                  controls
                  preload="metadata"
                  className="w-full max-h-[420px]"
                />
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-gray-400">
                  Video file not available yet.
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* A photo set has no duration or resolution/fps to show (Anshul, 2026-09-26:
                "no duration/fps tiles that would read as nonsense for photos") -- its own
                Photos tile takes their place instead. */}
            {video?.kind === 'photos' ? (
              <StatTile icon={PhotoIcon} label="Photos" value={formatNumber(photos.length)} />
            ) : (
              <>
                <StatTile icon={ClockIcon} label="Duration" value={formatDuration(video?.duration_s)} />
                <StatTile
                  icon={FilmIcon}
                  label="Resolution / fps"
                  value={
                    video?.width && video?.height
                      ? `${video.width}×${video.height}${video?.fps ? ` @ ${video.fps} fps` : ''}`
                      : '—'
                  }
                />
              </>
            )}
            <StatTile
              icon={ClipboardDocumentCheckIcon}
              label="Findings"
              value={formatNumber(video?.finding_count ?? 0)}
            />
            <StatTile
              icon={BookOpenIcon}
              label="Answer key entries"
              value={formatNumber(video?.answer_key?.length ?? 0)}
            />
          </div>

          <RunProgressPanel
            status={status}
            video={video}
            onStart={(force) => startMutation.mutate(force)}
            isStarting={startMutation.isLoading}
            writingReport={writingReport}
            isAdmin={isAdmin}
            demoLocked={demoLocked}
          />

          {status?.state === 'done' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">This run is done</h3>
                <p className="text-sm text-gray-500 mt-0.5" data-testid="report-state">
                  {reportStateText}
                </p>
              </div>
              {/* Both are equally ready the moment a run finishes; the report is the
                  auditor's actual deliverable and used to be reachable only through the
                  header-strip tab, several clicks below this panel. */}
              <div className="flex items-center gap-2">
                {FEATURES.projectReports && reportHasRows && (
                  <Link
                    to={`/projects/${projectId}/videos/${videoId}/report`}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-[#F8F8FF]"
                  >
                    <DocumentTextIcon className="h-4 w-4 mr-2" />
                    View report
                  </Link>
                )}
                {FEATURES.projectReview && (
                  <Link
                    to={`/projects/${projectId}/videos/${videoId}/review`}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Open Review Queue
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Anshul, 2026-09-26: "the auditor account shows no cost, tokens or usage
              metrics ... remove that from auditor account flow" -- admins keep it. */}
          {isAdmin && <CostPanel usage={findingsDoc?.usage || status?.usage} />}

          <ShotLog videoId={videoId} enabled={status?.state === 'done'} videoKind={video?.kind} />

          <AnswerKeyTable entries={video?.answer_key} videoKind={video?.kind} />
        </div>
      )}
    </div>
  );
}
